import type {SpanJSON, TransactionEvent} from "@sentry/core";

/**
 * Ops describing how the browser fetched the page rather than anything we can act on.
 *
 * These are whole op names, not descriptions: v8 bakes the label into the op itself
 * (`` op: `browser.${name}` ``, browserMetrics.js:413), so there is no op `browser` carrying a
 * `TLS/SSL` description to match on.
 *
 * `browser.request` and `browser.response` are deliberately kept — they measure our own server.
 */
const IgnoredSpanOps: ReadonlySet<string> = new Set([
  'mark',            // Angular's `performance.mark('mark_feature_usage')`, and Sentry's own 'sentry-tracing-init'
  'browser.TLS/SSL',
  'browser.cache',
  'browser.DNS',
]);

/**
 * One span per font, css, svg, js and json the page touches — the bulk of the volume, and none of it
 * actionable. The real ops are `resource.script|link|css|img|other`; `resource.fetch` and
 * `resource.xmlhttprequest` never occur, as the SDK skips those entries (browserMetrics.js:473).
 *
 * The known cost: web vitals survive this untouched (they live on `event.measurements`, not on a
 * span), but LCP is usually caused by one specific `resource.img`, and dropping the span drops the
 * name of the offender.
 */
const IgnoredSpanOpPrefixes = ['resource.'];

/**
 * Our own `DataService` lookups. Almost every one is a cache hit that resolves synchronously, and the
 * calls that do miss already produce an `http.client` span for the request they make.
 */
const IgnoredDataOps: ReadonlySet<string> = new Set([
  'data.getConditions',
  'data.getClinician',
  'data.getInsurer',
]);

/** The op of `AnalyticsService`'s flush transaction. Matched on op so the display name stays free. */
const FlushOp = 'analytics.flush';

const AngularRoutingOp = 'ui.angular.routing';
const RetainedRoutingPath = '/booking';

/**
 * True for the only routing spans worth keeping. The SDK sets the span name to the raw router URL —
 * query string and fragment included (`tracing.mjs:84`) — so `/booking?insurerId=4` must still match.
 * Parsed against an origin rather than split by hand, matching `isThirdPartyTelemetryUrl`.
 *
 * The `/` on the prefix keeps a hypothetical `/bookings` route from sneaking through.
 */
function isRetainedRoutingSpan(description: string | undefined): boolean {
  if (!description) {
    return false;
  }
  try {
    const {pathname} = new URL(description, window.location.origin);
    return pathname === RetainedRoutingPath || pathname.startsWith(`${RetainedRoutingPath}/`);
  } catch {
    return false;
  }
}

/** True when a span is page-load plumbing that should be stripped from a transaction before it is sent. */
export function isIgnoredSpan(span: SpanJSON): boolean {
  const op = span.op;
  if (!op) {
    return false;
  }
  if (op === AngularRoutingOp) {
    return !isRetainedRoutingSpan(span.description);
  }
  return IgnoredSpanOps.has(op)
    || IgnoredDataOps.has(op)
    || IgnoredSpanOpPrefixes.some(prefix => op.startsWith(prefix));
}

/**
 * True when an ignored data lookup ended up as the root of its own trace — `startSpan` with no active
 * parent starts a new trace, so a `DataService` call outside a navigation is sent as a transaction and
 * never appears in any `event.spans` array.
 */
export function isIgnoredRootTransaction(event: TransactionEvent): boolean {
  const op = event.contexts?.trace?.op;
  return !!op && IgnoredDataOps.has(op);
}

/**
 * True when a transaction is an analytics flush that succeeded — a user typing into a form flushes
 * every second, and a flush that worked tells us nothing. Failures are kept.
 *
 * Success is the *absence* of a status rather than `'ok'`: `getStatusMessage` maps an unset status to
 * `undefined` and `dropUndefinedKeys` then strips the key entirely (spanUtils.js:32,171). A flush that
 * failed calls `setStatus({code: 2})` with no message, which serializes as `'unknown_error'`.
 */
export function isSuccessfulFlushTransaction(event: TransactionEvent): boolean {
  const trace = event.contexts?.trace;
  return trace?.op === FlushOp && trace.status === undefined;
}

/**
 * True when `url` points at one of `telemetryHosts` — a third-party collector whose beacons we don't
 * want spans for.
 *
 * Compares hostnames rather than pattern-matching the URL: a `/piwik\.pro|\.php/` regex would also
 * match any of our own URLs that merely mention piwik.pro in a query param, or any `.php` we serve.
 * `url` may be relative, hence the `window.location.origin` base.
 */
export function isThirdPartyTelemetryUrl(url: string, telemetryHosts: readonly string[]): boolean {
  if (!telemetryHosts.length) {
    return false;
  }
  try {
    return telemetryHosts.includes(new URL(url, window.location.origin).hostname);
  } catch {
    // An unparseable URL is not something we can attribute to a telemetry host.
    return false;
  }
}
